import * as React from 'react'
import { format, isEqual, startOfDay } from 'date-fns'
import { parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'

import {
  startOfCurrentRange,
  type CalendarRangeDays,
} from '@/features/appointments/components/AppointmentsCalendar'
import { APPOINTMENTS_TABLE_ID } from '@/features/appointments/components/appointmentsTableSchema'
import type { AppointmentsView } from '@/features/appointments/utils/openSlotRows'
import { parseAppointmentDate } from '@/features/appointments/utils/parseAppointmentDate'

const VIEWS = [
  'table',
  'calendar',
] as const satisfies readonly AppointmentsView[]
const RANGES = ['3', '7'] as const

/**
 * URL params for the Appointments view selection. Namespaced with the table
 * id for the same reason the filter params are (see `namespaceUrlKeys` in the
 * nuqs adapter): the sidebar carries `location.search` across tabs, so a bare
 * `view`/`range`/`date` would be read by any other tab that later grows a view
 * toggle of its own.
 *
 * Spelled out here and nowhere else — `AppointmentsSheetDetails`' copy-link
 * builder picks them up for free by cloning `window.location.search`.
 */
export const APPOINTMENTS_VIEW_PARAM = `${APPOINTMENTS_TABLE_ID}.view`
export const APPOINTMENTS_RANGE_PARAM = `${APPOINTMENTS_TABLE_ID}.range`
export const APPOINTMENTS_DATE_PARAM = `${APPOINTMENTS_TABLE_ID}.date`

/** Serialization of `appointments.date`: a bare calendar day, no zone. */
const DATE_PARAM_FORMAT = 'yyyy-MM-dd'

const VIEW_STATE_PARSERS = {
  // Both toggles fall back to their default on an unrecognised value rather
  // than erroring, so a hand-trimmed or stale link still lands on a usable
  // screen instead of a blank one.
  view: parseAsStringLiteral(VIEWS).withDefault('table'),
  range: parseAsStringLiteral(RANGES).withDefault('3'),
  // Deliberately not a date parser: nuqs re-parses on every render, and a
  // fresh Date identity each time would churn every memo keyed on the visible
  // range (both calendar queries among them). Parsed once, below.
  date: parseAsString,
}

const VIEW_STATE_URL_KEYS = {
  view: APPOINTMENTS_VIEW_PARAM,
  range: APPOINTMENTS_RANGE_PARAM,
  date: APPOINTMENTS_DATE_PARAM,
}

/**
 * The Appointments page's view selection, held in the URL so it is visible,
 * reloadable and shareable: which view is up (`appointments.view`), and for
 * the calendar, its range mode (`appointments.range`) and first visible day
 * (`appointments.date`).
 *
 * Each param is omitted while it matches its default — Table view, a 3-day
 * window, the window around today — so the page's resting URL stays clean and
 * a link only carries what it needs to reproduce the screen. `date` in
 * particular is absent rather than pinned to today's date whenever the user
 * is on the current window, so a link shared from there means "the current
 * window" tomorrow too, not "the window around the day I shared it". For the
 * same reason the two calendar-only params are dropped when the view leaves
 * the calendar, rather than lingering on a Table-view URL.
 *
 * Written with `history: 'replace'` to match the filter params and the
 * `details` flag: paging the calendar a week at a time should not bury the
 * page the user arrived from under a stack of history entries.
 */
export function useAppointmentsViewState() {
  const [{ view, range, date }, setViewState] = useQueryStates(
    VIEW_STATE_PARSERS,
    { urlKeys: VIEW_STATE_URL_KEYS, history: 'replace' }
  )

  const rangeDays: CalendarRangeDays = range === '7' ? 7 : 3

  // An absent (or unparseable) `date` means "the window around today", which
  // is today-relative and so cannot be a static parser default. `startOfDay`
  // normalizes a hand-typed full timestamp down to its calendar day rather
  // than letting a time-of-day offset shift every column in the grid.
  const rangeStart = React.useMemo(() => {
    const parsed = parseAppointmentDate(date)
    return parsed ? startOfDay(parsed) : startOfCurrentRange(rangeDays)
  }, [date, rangeDays])

  // Leaving the calendar drops the calendar-only params with it: they describe
  // a grid that is no longer on screen, so a link copied from Table view would
  // otherwise carry coordinates that contradict what the recipient sees (and a
  // later return to the calendar would resume that stale week rather than
  // today). The accepted cost is that Table -> Calendar does not restore where
  // the user left the grid; "no params" meaning "the current window" is worth
  // more than that, and it is what keeps the URL an honest description of the
  // screen.
  const setView = React.useCallback(
    (next: AppointmentsView) => {
      void setViewState({
        view: next,
        ...(next === 'calendar' ? {} : { range: null, date: null }),
      })
    },
    [setViewState]
  )

  // Switching range mode recenters on today, so "Week" never opens on a stale
  // 3-day offset. Clearing `date` (rather than writing today's) is what keeps
  // "no date param" meaning "the current window".
  const setRangeDays = React.useCallback(
    (next: CalendarRangeDays) => {
      void setViewState({ range: next === 7 ? '7' : '3', date: null })
    },
    [setViewState]
  )

  // Paging to (or pressing Today to return to) the current window drops the
  // param instead of pinning the date, so the URL is clean whenever the screen
  // is in its default state.
  const setRangeStart = React.useCallback(
    (next: Date) => {
      const day = startOfDay(next)
      void setViewState({
        date: isEqual(day, startOfCurrentRange(rangeDays))
          ? null
          : format(day, DATE_PARAM_FORMAT),
      })
    },
    [rangeDays, setViewState]
  )

  return { view, setView, rangeDays, setRangeDays, rangeStart, setRangeStart }
}
